// Oliver Sweeney — Quick Add / Quick View popup
//
// Triggers:
//   [data-quick-add="handle"]           — single product
//   [data-quick-add="handle1,handle2"]  — multiple products side by side
//
// External API:
//   window.OSQuickAdd.open('handle')
//   window.OSQuickAdd.open(['handle1', 'handle2'])
//   window.OSQuickAdd.open('handle', { properties: { foo: 'bar' } })
//     — optional 2nd arg; its `properties` object is attached, unchanged,
//       to every line item this popup adds to the cart.
//
// Depends on: os-cart.js (OSCart)

class OSQuickAddPopup {
  constructor() {
    this._modal    = document.getElementById('os-quick-add')
    this._body     = this._modal?.querySelector('.os-quick-add__body')
    this._overlay  = this._modal?.querySelector('.os-quick-add__overlay')
    this._closeBtn = this._modal?.querySelector('.os-quick-add__close')
    this._panel    = this._modal?.querySelector('.os-quick-add__panel')
    this._cache    = {}
  }

  init() {
    if (!this._modal) return

    document.addEventListener('click', e => {
      const trigger = e.target.closest('[data-quick-add]')
      if (!trigger) return
      e.preventDefault()
      const handles = trigger.dataset.quickAdd.split(',').map(h => h.trim()).filter(Boolean)
      this.open(handles.length === 1 ? handles[0] : handles)
    })

    this._overlay.addEventListener('click',  () => this._close())
    this._closeBtn.addEventListener('click', () => this._close())
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this._modal.classList.contains('os-quick-add--open')) this._close()
    })

  }

  // ─── Public API ────────────────────────────────────────────────────────────

  async open(handles, opts) {
    const list       = Array.isArray(handles) ? handles : [handles]
    const properties = opts?.properties || null
    if (!list.length || !this._modal) return

    this._panel.dataset.count = Math.min(list.length, 5)
    this._showLoading()
    this._openModal()

    try {
      const docs = await Promise.all(list.map(h => this._fetchProduct(h)))
      this._renderAll(docs.map((doc, i) => ({ doc, handle: list[i], properties })))
    } catch {
      this._body.innerHTML = '<p class="os-quick-add__error">Sorry, could not load this product.</p>'
    }
  }

  // ─── Fetch ─────────────────────────────────────────────────────────────────

  async _fetchProduct(handle) {
    if (this._cache[handle]) return this._cache[handle]
    const res  = await fetch(`/products/${handle}?view=quick-add&_=${Date.now()}`)
    const html = await res.text()
    const doc  = new DOMParser().parseFromString(html, 'text/html')
    this._cache[handle] = doc
    return doc
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  _renderAll(products) {
    this._body.innerHTML = '<div class="os-qa-grid"></div>'
    const grid = this._body.querySelector('.os-qa-grid')
    products.forEach(({ doc, handle, properties }) => grid.appendChild(this._buildPanel(doc, handle, properties)))
  }

  _buildPanel(doc, handle, properties) {
    const meta       = doc.querySelector('[data-qa]')
    const imgUrl     = meta?.dataset.qaImage  || ''
    const title      = doc.querySelector('[data-qa-title]')?.textContent.trim() || ''
    const price      = parseInt(meta?.dataset.qaPrice   || '0')
    const compare    = parseInt(meta?.dataset.qaCompare || '0')
    const firstId    = meta?.dataset.qaVariant || ''
    const variantsEl   = doc.querySelector('[data-variants]')
    const variantsJson = doc.querySelector('.qa-variants-json')
    const customerNote  = doc.querySelector('[data-qa-customer-note]')?.innerHTML  || ''
    const promotionNote = doc.querySelector('[data-qa-promotion-note]')?.innerHTML || ''

    const onSale = compare > price

    const panel = document.createElement('div')
    panel.className = 'os-qa-panel'
    panel._properties = properties
    if (variantsJson) {
      try { panel._variants = JSON.parse(variantsJson.textContent) } catch {}
    }
    panel.innerHTML = `
      <div class="os-qa-header">
        ${imgUrl ? `<a href="/products/${handle}" class="os-qa-thumb-link" tabindex="-1"><img class="os-qa-thumb" src="${imgUrl}" alt="${title}"></a>` : ''}
        <div class="os-qa-meta">
          <h2 class="os-qa-title">${title}</h2>
          <div class="os-qa-price">
            ${onSale ? `<span class="product-price--original">${OSHelpers.formatMoney(compare)}</span>` : ''}
            <span class="${onSale ? 'product-price--sale' : ''}">${OSHelpers.formatMoney(price)}</span>
          </div>
          <a class="os-qa-link" href="/products/${handle}">Full Details</a>
        </div>
      </div>
      <div class="os-qa-variants"></div>
      ${customerNote ? `<div class="customerNote">${customerNote}</div>` : ''}
      ${promotionNote ? `<div class="customerNote">${promotionNote}</div>` : ''}
      <button class="os-qa-atc btn btn--bg-primary" disabled>Select a Size</button>
    `

    panel.querySelector('.os-qa-atc').addEventListener('click', () => this._addToCart(panel))

    const variantsWrap = panel.querySelector('.os-qa-variants')

    if (variantsEl) {
      const variantsClone = variantsEl.cloneNode(true)
      variantsWrap.appendChild(variantsClone)
      window.initComponents?.(variantsWrap)
      variantsClone.addEventListener('click', e => {
        const v = e.target.closest('.variant__option:not(.disabled)')
        if (v) this._selectVariant(v, panel)
      })
    } else {
      const atc = panel.querySelector('.os-qa-atc')
      atc.dataset.variant = firstId
      atc.disabled        = false
      atc.textContent     = 'Add to Bag'
    }

    return panel
  }

  // ─── Interactions ──────────────────────────────────────────────────────────

  _selectVariant(el, panel) {
    // Clear selection only within the clicked option group
    el.closest('.variant__options')?.querySelectorAll('.variant__option').forEach(v => v.classList.remove('selected'))
    el.classList.add('selected')

    const atc       = panel.querySelector('.os-qa-atc')
    const allGroups = [...panel.querySelectorAll('.variant__options[data-option-index]')]

    // Single-option: the clicked element's data-variant is the final answer
    if (allGroups.length <= 1) {
      atc.dataset.variant = el.dataset.variant
      atc.disabled        = false
      atc.textContent     = 'Add to Bag'
      return
    }

    // Multi-option (e.g. waist + leg length):
    // After each selection, recalculate which options in every group are available
    // given what is currently selected in the other groups.
    const getSelVal = g => g.querySelector('.variant__option.selected')?.dataset.optionValue || null
    let selectedValues = allGroups.map(getSelVal)

    if (panel._variants) {
      allGroups.forEach((group, groupIdx) => {
        const optIdx = parseInt(group.dataset.optionIndex)
        group.querySelectorAll('.variant__option').forEach(btn => {
          const btnValue = btn.dataset.optionValue
          if (!btnValue) return
          const available = panel._variants.some(v => {
            if (v[`option${optIdx}`] !== btnValue || !v.available) return false
            return allGroups.every((g, i) => {
              if (i === groupIdx) return true       // don't constrain own group
              const selVal = selectedValues[i]
              return !selVal || v[`option${parseInt(g.dataset.optionIndex)}`] === selVal
            })
          })
          if (!available) {
            btn.classList.add('disabled')
            if (btn.classList.contains('selected')) btn.classList.remove('selected')
          } else {
            btn.classList.remove('disabled')
          }
        })
      })
      // Re-collect in case a previously-selected button became disabled
      selectedValues = allGroups.map(getSelVal)
    }

    if (selectedValues.some(v => v === null)) {
      atc.disabled    = true
      atc.textContent = 'Select a Size'
      delete atc.dataset.variant
      return
    }

    const finalVariant = panel._variants?.find(v =>
      allGroups.every((g, i) => v[`option${parseInt(g.dataset.optionIndex)}`] === selectedValues[i])
    )

    if (finalVariant) {
      atc.dataset.variant = finalVariant.id
      atc.disabled        = !finalVariant.available
      atc.textContent     = finalVariant.available ? 'Add to Bag' : 'Out of Stock'
    }
  }

  async _addToCart(panel) {
    const atc       = panel.querySelector('.os-qa-atc')
    const variantId = parseInt(atc?.dataset.variant)
    if (!variantId) return
    atc.disabled    = true
    atc.textContent = 'Adding…'
    try {
      const payload = { id: variantId, quantity: 1 }
      if (panel._properties) payload.properties = panel._properties
      await OSCart.addItem(payload)
      atc.textContent = 'Added to Bag!'
      setTimeout(() => {
        atc.textContent = 'Add to Bag'
        atc.disabled    = false
        delete atc.dataset.variant
        panel.querySelectorAll('.variant__option.selected').forEach(v => v.classList.remove('selected'))
      }, 1800)
    } catch {
      atc.textContent = 'Error — try again'
      atc.disabled    = false
    }
  }

  // ─── Modal state ───────────────────────────────────────────────────────────

  _showLoading() {
    this._body.innerHTML = `
      <div class="os-quick-add__loading">
        <span></span><span></span><span></span>
      </div>
    `
  }

  _openModal() {
    this._modal.setAttribute('aria-hidden', 'false')
    this._modal.classList.add('os-quick-add--open')
    document.body.classList.add('quick-add-open')
    this._closeBtn.focus()
  }

  _close() {
    this._modal.setAttribute('aria-hidden', 'true')
    this._modal.classList.remove('os-quick-add--open')
    document.body.classList.remove('quick-add-open')
  }
}

const _qaInstance = new OSQuickAddPopup()
document.addEventListener('DOMContentLoaded', () => _qaInstance.init())

window.OSQuickAdd = { open: (handles, opts) => _qaInstance.open(handles, opts) }
